import { Stack } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import CompanyInfoSession from "../Component/CompanyInfoSection";
import MerchantInfoSession from "../Component/MerchantInfoSection";
import Divider from "components/Divider/Divider";
import OthersSession from "../Component/OthersSection";
import AccountInfoSession from "../Component/AccountInfoSection";
import { useTranslation } from "react-i18next";
import { updateMerchant, updateMerchantOtherInfo } from "../model";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import { useSnackbar } from "notistack";
import { INFO_KEYS, pickTheFieldsFrom, filterDifferences } from "../utils";
import useFormUndo from "utils/useFormUndo/useFormUndo";
import { formPreventEnterSubmit } from "utils/utils";
import usePageWrapper from "utils/usePageWrapper/usePageWrapper";
import { FormBuilder } from "components/FormBuilder/FormBuilder";

const PAGE_LOCALE_KEY = "page.merchants.merchantDetail.";

/**
 * @param {Object} props
 * @param {import("utils/usePageWrapper/usePageWrapper").CallApisFn<import("../OfflineMerchantDetail").offlineMerchantDetailPartitionKeyType>} props.callApis 
 * @returns 
 */
const MerchantInfo = ({
  callApis,
  merchant,
  merchantOtherDetail,
  dropdownOptions,
  staticMerchantPayload,
  fetchMerchantAndMerchantOtherDetail,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const formProps = useForm({
    mode: "onChange",
  });
  const {
    handleSubmit,
    reset,
    setValue,
  } = formProps;
  const { rememberForm, undoForm, clearFormMemory } = useFormUndo({ formProps });
  const { PageStickyFooter } = usePageWrapper();

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [originalLogo, setOriginalLogo] = useState(null);

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  useEffect(() => {
    if (Object.keys(merchant).length > 0) {
      console.log("[INFO] merchant: ", merchant);
      const initialValue = {
        ...pickTheFieldsFrom(merchant, INFO_KEYS()),
        ...pickTheFieldsFrom(merchantOtherDetail, INFO_KEYS()),
      };
      console.log("[INFO]initialValue: ", initialValue);
      setAllValues(initialValue);
    }
  }, [merchant]);

  const onSubmit = async (formValue) => {
    const MERCHANT_OTHER_INFO_KEYS = [
      "onboardSales",
      "sales",
      "referrer",
      "bankAccount",
      "beneficiary",
      "bankAccountRemarks",
    ];

    try {
      setIsLoading(true);

      const apiConfigs = [
        {
          key: "MERCHANT",
          diff: filterDifferences(
            merchant,
            formValue,
            null,
            MERCHANT_OTHER_INFO_KEYS,
          ),
          payload: {
            ...staticMerchantPayload(merchant, "info"),
            ...filterDifferences(
              merchant,
              formValue,
              null,
              MERCHANT_OTHER_INFO_KEYS,
            ),
          },
          apiCall: updateMerchant,
        },
        {
          key: "MERCHANT_OTHER_INFO",
          diff: filterDifferences(
            merchantOtherDetail,
            formValue,
            MERCHANT_OTHER_INFO_KEYS,
            [],
          ),
          payload: {
            ...filterDifferences(
              merchantOtherDetail,
              formValue,
              MERCHANT_OTHER_INFO_KEYS,
              [],
            ),
            merchantId: merchant.merchantId,
            id: merchantOtherDetail?.id,
            ...(formValue.sales && { sales: { id: formValue.sales } }),
            ...(formValue.onboardSales && {
              onboardSales: { id: formValue.onboardSales },
            }),
            ...(formValue.referrer && { referrer: { id: formValue.referrer } }),
          },
          apiCall: updateMerchantOtherInfo,
        },
      ];

      const validApis = apiConfigs.filter((config) => {
        console.log(`${config.key} config.diff`, config.diff);
        return Object.keys(config.diff).length > 0;
      });

      if (validApis.length === 0) {
        snackbar(t(PAGE_LOCALE_KEY + "fetchMessage.noChanges"), "info");
        return;
      }

      const [...resps] = await callApis([
        ...validApis.map((config) => ({
          api: config.apiCall,
          payload: config.payload,
          blockOnError: false,
          blockOnUnauthorized: false,
        })),
      ]);

      let isAllSuccess = true;
      let isSomeError = false;

      resps.forEach((resp) => {
        !resp.isSuccess && (isAllSuccess = false);
        resp.isError && (isSomeError = true);
      })

      if (isAllSuccess && validApis.length > 0) {
        snackbar(t(PAGE_LOCALE_KEY + "fetchMessage.updateSuccess"), "success");
        await fetchMerchantAndMerchantOtherDetail();
        setIsEditing(false);
        clearFormMemory();
      } else {
        isSomeError && snackbar(
          t(PAGE_LOCALE_KEY + "fetchMessage.updateFailed"),
          "error",
        );
      }
    } catch (error) {
      console.error("Submission error:", error);
      snackbar(
        `${t(PAGE_LOCALE_KEY + "fetchMessage.updateFailed")}: ${error.message}`,
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };
  const appendBase64Data = (base64String) => {
    if (base64String?.startsWith("iVBORw0KGgo")) {
      base64String = "data:image/png;base64," + base64String;
    }
    if (base64String?.startsWith("/9j/")) {
      base64String = "data:image/jpeg;base64," + base64String;
    }
    return base64String;
  };

  const setAllValues = (initialValue) => {
    const logoValue = appendBase64Data(initialValue?.logo);
    setOriginalLogo(logoValue); // Store original logo value
    reset(
      {
        ...initialValue,
        shareholderInfo:
          typeof initialValue?.shareholderInfo === "string"
            ? JSON.parse(initialValue?.shareholderInfo ?? "[]")
            : initialValue?.shareholderInfo,
        logo: logoValue,
        referrer: initialValue?.referrer?.id,
        onboardSales: initialValue?.onboardSales?.id,
        sales: initialValue?.sales?.id,
      },
      {
        keepDefaultValues: false,
        keepDirty: false,
        keepErrors: false,
      },
    );
  };

  const submitButtons = () => (
    <Stack
      direction="row"
      justifyContent="flex-end"
      gap={2}
    >
      {
        !isEditing &&
        <ButtonMAS
          onClick={() => {
            setIsEditing(true);
            rememberForm();
          }}
        >
          {t(`${PAGE_LOCALE_KEY}button.edit`)}
        </ButtonMAS>

      }
      {isEditing && <>
        <ButtonMAS
          variant="bordered"
          type="button" // Add type="button" to prevent form submission
          onClick={() => {
            // Restore original logo when canceling (only if there was one)
            originalLogo
              ? setValue("logo", originalLogo)
              : setValue("logo", null);
            setIsEditing(false);
            undoForm();
          }}
          disabled={isLoading}
        >
          {t(`${PAGE_LOCALE_KEY}button.cancel`)}
        </ButtonMAS>
        <ButtonMAS
          variant="primary"
          type="submit"
          isLoading={isLoading}
        >
          {t(`${PAGE_LOCALE_KEY}button.save`)}
        </ButtonMAS>
      </>
      }
    </Stack>
  )

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)} onKeyDown={formPreventEnterSubmit}>
      <FormBuilder.Section>
        <CompanyInfoSession
          formProps={formProps}
          isLoading={isLoading}
          isEdit={isEditing}
          mccDropdownOptions={dropdownOptions?.mccList}
        />
        <Divider />

        <MerchantInfoSession
          formProps={formProps}
          isLoading={isLoading}
          isEdit={isEditing}
        />
        <Divider />

        <OthersSession
          formProps={formProps}
          isLoading={isLoading}
          isEdit={isEditing}
          dropdownOptions={dropdownOptions}
        />
        <Divider />

        <AccountInfoSession
          formProps={formProps}
          isLoading={isLoading}
          isEdit={isEditing}
        />

        <PageStickyFooter>
          {submitButtons()}
        </PageStickyFooter>
      </FormBuilder.Section>
    </form>
  );
};

export default React.memo(MerchantInfo);
